import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Funnel, Lock, MagnifyingGlass, Plus } from "@phosphor-icons/react";
import {
  REQUEST_STATUSES,
  STATUS_LABEL,
  isClosedStatus,
  useRequestMonthUsage,
  type ClientRequest
} from "../../data/requestQueries";
import { ClientHead } from "../../ui/ClientHead";
import { Panel } from "../../ui/Panel";
import { BoxState } from "../../ui/BoxState";
import { Segmented } from "../../ui/Segmented";
import { RowLink, RowList } from "../../ui/RowLinks";
import { OverCapTag, StatusWord } from "./requestWords";
import { NewRequestSheet } from "./NewRequestSheet";
import { useClientPath, useViewOnly } from "../../data/readSource";

/**
 * `/requests`: one box, one row per request, the whole row a link to the request's own page
 * (`prototype/kund/pages.js` `requestsPage`, approved 2026-09-24).
 *
 * The row is the number, small, above the title, and the status on the right as a grey word, except
 * "Väntar på er", which is the one status that asks something of the client and so the only one
 * with colour (design.md section 3, rule 2). The updated time and the service the old row carried
 * are gone from it: the time is in the request's own timeline, and the service is on its page and
 * in the filter.
 *
 * The toolbar is the sketch's: Aktiva / Klara and a search. The finer status and the service are
 * behind one Filter button, the admin's pattern of the same day, because the feature map keeps both
 * and the toolbar has to stay one line on a phone. The service filter is offered only when there
 * is more than one service, since a filter with one answer is a question nobody needs to be asked.
 *
 * "X av Y visas" is not drawn. The number of open requests is Översikt's to show and nobody else's
 * (rule 1), and the list itself is the count of what the filter left.
 *
 * Build round A2, 2026-10-08: one line above the list, "Små ändringar den här månaden: 2 av 3". It is
 * not a count of the list, which is why rule 1 does not strike it: it is the month's use of the
 * förvaltning, read from `public.client_request_month_usage`, and no other screen shows it. It is
 * secondary, so it waits silently: absent while it loads, absent when the client has no förvaltning
 * this month, and absent when the read fails, because a missing line is true and an error box over a
 * list that works is not worth it. It is never drawn from a stale answer once a later read has failed.
 * A request still waiting for Petrovic's choice over the cap carries "Över månadens tak" under its
 * title.
 */

type Segment = "active" | "completed";

const SEGMENTS: ReadonlyArray<{ value: Segment; label: string }> = [
  { value: "active", label: "Aktiva" },
  { value: "completed", label: "Klara" }
];

/**
 * The finer statuses inside Aktiva. Klart is the other segment, so it is not offered twice, and
 * neither is Avböjt since build round A1 (2026-10-08): both final statuses are under Klara.
 */
const ACTIVE_STATUSES = REQUEST_STATUSES.filter((status) => !isClosedStatus(status));

const ALL = "all";

export function RequestsListPage({
  rows,
  services,
  tenantId,
  userId,
  writable
}: {
  rows: ClientRequest[];
  services: ReadonlyArray<{ id: string; display_name: string }>;
  tenantId: string;
  userId: string | null;
  writable: boolean;
}) {
  const navigate = useNavigate();
  const viewOnly = useViewOnly();
  const clientPath = useClientPath();
  const [params, setParams] = useSearchParams();
  const usage = useRequestMonthUsage(tenantId);
  // `isSuccess` rather than `data`: a refetch that fails keeps the old answer in `data`, and a number
  // the portal could not confirm is not shown.
  const month = usage.isSuccess && usage.data && usage.data.cap !== null ? usage.data : null;

  /*
   * The segment lives in the url, `?status=klara` as in the sketch, so the way back from a finished
   * request (its back link carries the same parameter) lands in the list that holds it. Search and
   * the finer filters are the reader's working state and stay in the component.
   */
  const segment: Segment = params.get("status") === "klara" ? "completed" : "active";
  const setSegment = (next: Segment) => {
    setParams(next === "completed" ? { status: "klara" } : {}, { replace: true });
  };

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [serviceFilter, setServiceFilter] = useState<string>(ALL);

  const choosesService = services.length > 1;
  // A service filter left set while the tenant had two services must not keep hiding rows once the
  // picker is gone, so the effective value is decided from what is on screen.
  const effectiveService = choosesService ? serviceFilter : ALL;
  const effectiveStatus = segment === "active" ? statusFilter : ALL;
  const narrowed = effectiveService !== ALL || effectiveStatus !== ALL;

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (segment === "completed" ? !isClosedStatus(row.status) : isClosedStatus(row.status)) {
        return false;
      }
      if (effectiveStatus !== ALL && row.status !== effectiveStatus) {
        return false;
      }
      if (effectiveService !== ALL && row.service_id !== effectiveService) {
        return false;
      }
      if (term.length === 0) {
        return true;
      }
      return (
        String(row.request_number).includes(term) ||
        (row.title ?? "").toLowerCase().includes(term)
      );
    });
  }, [rows, search, segment, effectiveStatus, effectiveService]);

  /*
   * Nytt ärende, and where focus goes when it closes without a request. The sheet is a modal
   * `<dialog>`, and while it is open the page behind it is inert, so the button cannot take focus
   * back until the dialog has left the document. The effect runs after that commit. A sent request
   * navigates to its own page instead, and there the shell's `RouteChrome` moves focus.
   */
  const [composing, setComposing] = useState(false);
  const opener = useRef<HTMLButtonElement | null>(null);
  const returnFocus = useRef(false);

  useEffect(() => {
    if (!composing && returnFocus.current) {
      returnFocus.current = false;
      opener.current?.focus();
    }
  }, [composing]);

  // "Visa som kund" (build round 41): an open portal's button is absent, and "Läsläge" is kept for the
  // portal that really is read-only, because that is what the client reads.
  const newRequest = writable && viewOnly ? null : writable ? (
    <button
      ref={opener}
      type="button"
      className="button is-primary k-new-request-button"
      onClick={() => setComposing(true)}
    >
      <Plus size={16} aria-hidden="true" />
      <span>Nytt ärende</span>
    </button>
  ) : (
    /*
      In Läsläge the word takes the button's place (decided 2026-09-24). The disabled button that
      stood here said nothing about why it could not be pressed (FEATURE-MAP.md, question 9).
    */
    <span className="k-locked" data-testid="requests-locked">
      <Lock size={16} aria-hidden="true" />
      Läsläge
    </span>
  );

  const filters =
    segment === "active" || choosesService ? (
      <details className="filters k-filters">
        <summary className={`button is-quiet${narrowed ? " is-narrowed" : ""}`} data-testid="request-filters">
          <Funnel size={16} aria-hidden="true" />
          Filter
          {narrowed ? <span className="visually-hidden"> (används)</span> : null}
        </summary>
        <div className="filters-body">
          {segment === "active" ? (
            <label>
              Status
              {/*
                Named by `aria-label` as well as by the label around it, because a label that wraps
                its select also counts the chosen option, and the select would be "Status Alla aktiva"
                to a screen reader and to a test that asks for Status (found by the browser suite).
              */}
              <select
                aria-label="Status"
                className="box-select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value={ALL}>Alla aktiva</option>
                {ACTIVE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABEL[status]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {choosesService ? (
            <label>
              Tjänst
              <select
                aria-label="Tjänst"
                className="box-select"
                value={serviceFilter}
                onChange={(event) => setServiceFilter(event.target.value)}
              >
                <option value={ALL}>Alla tjänster</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.display_name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </details>
    ) : null;

  return (
    <div className="k-page k-requests">
      <ClientHead title="Ärenden" titleId="requests-title" actions={newRequest} />

      {month ? (
        <p className="k-requests-usage" data-testid="requests-month-usage">
          {`Små ändringar den här månaden: ${month.counted} av ${month.cap}`}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <Panel>
          <BoxState
            kind="empty"
            title="Här är det lugnt"
            body="När ni skickar ett ärende visas det här med status, kommentarer och bilagor."
          />
        </Panel>
      ) : (
        <>
          <div className="toolbar k-requests-toolbar" role="search" aria-label="Filtrera ärenden">
            <Segmented label="Visa ärenden" value={segment} options={SEGMENTS} onChange={setSegment} />
            <label className="box-search k-search">
              <MagnifyingGlass size={16} aria-hidden="true" />
              <span className="visually-hidden">Sök ärenden</span>
              <input
                type="search"
                placeholder="Nummer eller rubrik"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            {filters}
          </div>

          <Panel flush testId="request-list">
            {visible.length === 0 ? (
              <BoxState
                kind="empty"
                compact
                title="Inget ärende matchar filtret"
                body="Ändra sökningen eller välj en annan status."
              />
            ) : (
              <RowList label="Ärenden">
                {visible.map((row) => (
                  <RowLink
                    key={row.id}
                    to={clientPath(`/requests/${row.request_number}`)}
                    testId="request-row"
                    trail={<StatusWord status={row.status} />}
                  >
                    <span className="k-req">
                      <span className="k-req-number">REQ-{row.request_number}</span>{" "}
                      <strong className="k-req-title">{row.title ?? "Utan rubrik"}</strong>
                      {row.over_cap ? <OverCapTag /> : null}
                    </span>
                  </RowLink>
                ))}
              </RowList>
            )}
          </Panel>
        </>
      )}

      {composing ? (
        <NewRequestSheet
          tenantId={tenantId}
          userId={userId}
          onClose={() => {
            returnFocus.current = true;
            setComposing(false);
          }}
          onCreated={(created) => {
            setComposing(false);
            navigate(`/requests/${created}`);
          }}
        />
      ) : null}
    </div>
  );
}
